import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Box,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  makeStyles,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Tooltip
} from '@material-ui/core';
import {
  Close,
  Edit,
  Save,
  Cancel,
  LocationOn,
  Schedule,
  Person,
  Work,
  Business,
  Group,
  Image,
  History,
  CheckCircle,
  Warning,
  Opacity,
  Note
} from '@material-ui/icons';
import { format } from 'date-fns';
import axios from 'axios';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    maxWidth: 800,
    width: '100%'
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500]
  },
  tabContent: {
    padding: theme.spacing(3),
    minHeight: 400
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main
  },
  infoRow: {
    marginBottom: theme.spacing(1.5)
  },
  label: {
    fontWeight: 500,
    color: theme.palette.text.secondary
  },
  value: {
    color: theme.palette.text.primary
  },
  statusChip: {
    fontWeight: 'bold'
  },
  photoContainer: {
    position: 'relative',
    width: '100%',
    paddingTop: '75%',
    backgroundColor: theme.palette.grey[200],
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
    cursor: 'pointer'
  },
  photo: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  noPhoto: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: theme.palette.text.secondary
  },
  photoLabel: {
    textAlign: 'center',
    marginTop: theme.spacing(1),
    fontWeight: 500
  },
  mapContainer: {
    height: 300,
    backgroundColor: theme.palette.grey[200],
    borderRadius: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden'
  },
  historyItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  editField: {
    marginBottom: theme.spacing(2)
  },
  irrigationSection: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2)
  },
  approvalSection: {
    backgroundColor: '#fff8e1',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2)
  },
  approvalButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2)
  },
  reviveChip: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    fontWeight: 'bold'
  },
  removeChip: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    fontWeight: 'bold'
  },
  replaceChip: {
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    fontWeight: 'bold'
  },
  needsApprovalChip: {
    border: '1px dashed #f9a825',
    backgroundColor: '#fff8e1',
    color: '#f57f17',
    fontWeight: 'bold'
  },
  detailSubsection: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(1)
  }
}));

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`plant-details-tabpanel-${index}`}
      aria-labelledby={`plant-details-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

// No external map component needed

export default function PlantDetailsModal({ open, onClose, plant, onUpdate, apiUrl }) {
  const classes = useStyles();
  const [tabValue, setTabValue] = useState(0);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [editedPlant, setEditedPlant] = useState(plant);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleEdit = () => {
    setEditing(true);
    setEditedPlant({ ...plant });
  };

  const handleCancel = () => {
    setEditing(false);
    setEditedPlant(plant);
  };

  const handleFieldChange = (field, value) => {
    setEditedPlant(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleIrrigationChange = (field, value) => {
    setEditedPlant(prev => ({
      ...prev,
      irrigationDetails: {
        ...prev.irrigationDetails,
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await axios.put(
        `${apiUrl}/api/hmi/plant-tracking/${plant._id}`,
        editedPlant
      );
      
      if (response.data.success) {
        onUpdate(response.data.plant);
        setEditing(false);
      }
    } catch (error) {
      console.error('Error updating plant:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleApproveAction = async (action) => {
    try {
      setApproving(true);
      const response = await axios.put(
        `${apiUrl}/api/hmi/plant-tracking/${plant._id}`,
        { approvedAction: action }
      );
      if (response.data.success) {
        onUpdate(response.data.data);
      }
    } catch (error) {
      console.error('Error approving plant action:', error);
    } finally {
      setApproving(false);
    }
  };

  const getRecommendationChip = (rec) => {
    if (!rec) return null;
    const chipMap = {
      revive: classes.reviveChip,
      remove: classes.removeChip,
      replace: classes.replaceChip
    };
    return (
      <Chip
        size="small"
        label={rec.charAt(0).toUpperCase() + rec.slice(1)}
        className={chipMap[rec] || ''}
      />
    );
  };

  const getApprovedActionChip = (action) => {
    if (!action) {
      return (
        <Chip
          size="small"
          label="Pending"
          className={classes.needsApprovalChip}
          variant="outlined"
        />
      );
    }
    const chipMap = {
      revive: classes.reviveChip,
      remove: classes.removeChip,
      replace: classes.replaceChip
    };
    return (
      <Chip
        size="small"
        label={action.charAt(0).toUpperCase() + action.slice(1)}
        className={chipMap[action] || ''}
      />
    );
  };

  const getStatusColor = (state) => {
    const statusColors = {
      IDENTIFIED: '#2196f3',
      IMAGE_CAPTURED: '#ff9800',
      LOCATED: '#9c27b0',
      REMOVED: '#f44336',
      IRRIGATION_CONFIRMED: '#00bcd4',
      COMPLETED: '#4caf50',
      // Support both formats
      identified: '#2196f3',
      imageCaptured: '#ff9800',
      located: '#9c27b0',
      removed: '#f44336',
      irrigationConfirmed: '#00bcd4',
      completed: '#4caf50'
    };
    return statusColors[state] || '#757575';
  };

  const renderOverviewTab = () => (
    <Box className={classes.tabContent}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" className={classes.sectionTitle}>
            <Work /> Basic Information
          </Typography>
          
          <Box className={classes.infoRow}>
            <Typography className={classes.label}>Plant ID:</Typography>
            <Typography className={classes.value}>
              {plant.plantId || plant._id?.substring(0, 8)}
            </Typography>
          </Box>

          <Box className={classes.infoRow}>
            <Typography className={classes.label}>Type:</Typography>
            <Chip
              size="small"
              label={plant.type}
              style={{
                backgroundColor: plant.type === 'dead' ? '#ffcdd2' : '#ffe0b2',
                color: plant.type === 'dead' ? '#b71c1c' : '#e65100',
                fontWeight: 'bold'
              }}
            />
          </Box>

          <Box className={classes.infoRow}>
            <Typography className={classes.label}>Status:</Typography>
            <Chip
              size="small"
              label={plant.state}
              style={{
                backgroundColor: getStatusColor(plant.state),
                color: 'white',
                fontWeight: 'bold'
              }}
            />
          </Box>

          {editing ? (
            <>
              <TextField
                className={classes.editField}
                label="Quantity"
                type="number"
                fullWidth
                value={editedPlant.quantity || 1}
                onChange={(e) => handleFieldChange('quantity', parseInt(e.target.value))}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editedPlant.isCluster || false}
                    onChange={(e) => handleFieldChange('isCluster', e.target.checked)}
                  />
                }
                label="Is Cluster"
              />
              <FormControl fullWidth className={classes.editField}>
                <InputLabel>Approved Action</InputLabel>
                <Select
                  value={editedPlant.approvedAction || ''}
                  onChange={(e) => handleFieldChange('approvedAction', e.target.value || null)}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="revive">Revive</MenuItem>
                  <MenuItem value="remove">Remove</MenuItem>
                  <MenuItem value="replace">Replace</MenuItem>
                </Select>
              </FormControl>
            </>
          ) : (
            <>
              <Box className={classes.infoRow}>
                <Typography className={classes.label}>Quantity:</Typography>
                <Typography className={classes.value}>{plant.quantity || 1}</Typography>
              </Box>
              <Box className={classes.infoRow}>
                <Typography className={classes.label}>Is Cluster:</Typography>
                <Typography className={classes.value}>
                  {plant.isCluster ? 'Yes' : 'No'}
                </Typography>
              </Box>
            </>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="h6" className={classes.sectionTitle}>
            <Business /> Job Information
          </Typography>
          
          <Box className={classes.infoRow}>
            <Typography className={classes.label}>Job Site:</Typography>
            <Typography className={classes.value}>
              {plant.jobSite?.name || plant.jobSiteName || plant.jobSiteAddress || '-'}
            </Typography>
          </Box>

          <Box className={classes.infoRow}>
            <Typography className={classes.label}>Client:</Typography>
            <Typography className={classes.value}>
              {plant.client?.displayName || plant.clientName || plant.client?.name || '-'}
            </Typography>
          </Box>

          <Box className={classes.infoRow}>
            <Typography className={classes.label}>Work Order:</Typography>
            <Typography className={classes.value}>
              {(() => {
                // Handle different workorder formats
                if (typeof plant.workorder === 'string') {
                  return plant.workorder.substring(0, 8);
                } else if (plant.workorder?._id) {
                  return plant.workorder._id.substring(0, 8);
                } else if (typeof plant.workorderId === 'string') {
                  return plant.workorderId.substring(0, 8);
                } else {
                  return '-';
                }
              })()}
            </Typography>
          </Box>

          <Box className={classes.infoRow}>
            <Typography className={classes.label}>Crew:</Typography>
            <Typography className={classes.value}>
              {plant.crew?.name || '-'}
            </Typography>
          </Box>

          <Box className={classes.infoRow}>
            <Typography className={classes.label}>Reported By:</Typography>
            <Typography className={classes.value}>
              {plant.reportedByName || plant.reportedBy?.name || '-'}
            </Typography>
          </Box>
        </Grid>

        {/* Recommendation & Approval Section */}
        <Grid item xs={12}>
          <Box className={classes.approvalSection}>
            <Typography variant="h6" className={classes.sectionTitle} style={{ marginBottom: 12 }}>
              Recommendation & Approval
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography className={classes.label}>Crew Recommendation:</Typography>
                <Box mt={0.5}>
                  {plant.crewRecommendation
                    ? getRecommendationChip(plant.crewRecommendation)
                    : <Typography variant="body2" color="textSecondary">None</Typography>
                  }
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography className={classes.label}>Approved Action:</Typography>
                <Box mt={0.5}>
                  {getApprovedActionChip(plant.approvedAction)}
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography className={classes.label}>Approved By:</Typography>
                <Typography className={classes.value}>
                  {plant.approvedBy
                    ? `${plant.approvedBy.firstName} ${plant.approvedBy.lastName}`
                    : '--'}
                  {plant.approvedAt && (
                    <Typography variant="caption" display="block" color="textSecondary">
                      {format(new Date(plant.approvedAt), 'MM/dd/yyyy HH:mm')}
                    </Typography>
                  )}
                </Typography>
              </Grid>
            </Grid>

            {/* Approval action buttons when no approvedAction yet */}
            {plant.crewRecommendation && !plant.approvedAction && !editing && (
              <Box className={classes.approvalButtons}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={approving}
                  onClick={() => handleApproveAction('revive')}
                  style={{ backgroundColor: '#4caf50', color: 'white' }}
                >
                  Revive
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  disabled={approving}
                  onClick={() => handleApproveAction('remove')}
                  style={{ backgroundColor: '#f44336', color: 'white' }}
                >
                  Remove
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  disabled={approving}
                  onClick={() => handleApproveAction('replace')}
                  style={{ backgroundColor: '#1976d2', color: 'white' }}
                >
                  Replace
                </Button>
              </Box>
            )}

            {/* Revive details */}
            {plant.approvedAction === 'revive' && plant.revive && (
              <Box className={classes.detailSubsection}>
                <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: 8 }}>
                  Revive Details
                </Typography>
                {plant.revive.followUpResult && (
                  <Box className={classes.infoRow}>
                    <Typography className={classes.label}>Follow-up Result:</Typography>
                    <Typography className={classes.value}>{plant.revive.followUpResult}</Typography>
                  </Box>
                )}
                {plant.revive.actionTaken && (
                  <Box className={classes.infoRow}>
                    <Typography className={classes.label}>Action Taken:</Typography>
                    <Typography className={classes.value}>{plant.revive.actionTaken}</Typography>
                  </Box>
                )}
                {plant.revive.completedDate && (
                  <Box className={classes.infoRow}>
                    <Typography className={classes.label}>Completed:</Typography>
                    <Typography className={classes.value}>
                      {format(new Date(plant.revive.completedDate), 'MM/dd/yyyy')}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Replacement details */}
            {plant.approvedAction === 'replace' && plant.replacement && (
              <Box className={classes.detailSubsection}>
                <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: 8 }}>
                  Replacement Details
                </Typography>
                {plant.replacement.plantType && (
                  <Box className={classes.infoRow}>
                    <Typography className={classes.label}>Plant Type:</Typography>
                    <Typography className={classes.value}>{plant.replacement.plantType}</Typography>
                  </Box>
                )}
                {plant.replacement.notes && (
                  <Box className={classes.infoRow}>
                    <Typography className={classes.label}>Notes:</Typography>
                    <Typography className={classes.value}>{plant.replacement.notes}</Typography>
                  </Box>
                )}
                {plant.replacement.completedDate && (
                  <Box className={classes.infoRow}>
                    <Typography className={classes.label}>Completed:</Typography>
                    <Typography className={classes.value}>
                      {format(new Date(plant.replacement.completedDate), 'MM/dd/yyyy')}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" className={classes.sectionTitle}>
            <Schedule /> Timestamps
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(plant.timestamps || {}).map(([key, value]) => (
              value && (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <Box className={classes.infoRow}>
                    <Typography className={classes.label}>
                      {key.charAt(0).toUpperCase() + key.slice(1)}:
                    </Typography>
                    <Typography className={classes.value}>
                      {format(new Date(value), 'MM/dd/yyyy HH:mm')}
                    </Typography>
                  </Box>
                </Grid>
              )
            ))}
          </Grid>
        </Grid>

        {editing ? (
          <Grid item xs={12}>
            <TextField
              className={classes.editField}
              label="Notes"
              multiline
              rows={4}
              fullWidth
              value={editedPlant.notes || ''}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
            />
          </Grid>
        ) : (
          plant.notes && (
            <Grid item xs={12}>
              <Typography variant="h6" className={classes.sectionTitle}>
                <Note /> Notes
              </Typography>
              <Typography>{plant.notes}</Typography>
            </Grid>
          )
        )}
      </Grid>
    </Box>
  );

  const renderPhotosTab = () => (
    <Box className={classes.tabContent}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Box className={classes.photoContainer}>
            {(plant.photos?.before?.url || plant.photos?.before?.uri || plant.photo?.cloudUrl || plant.photo?.uri) ? (
              <>
                <img
                  src={plant.photos?.before?.url || plant.photos?.before?.uri || plant.photo?.cloudUrl || plant.photo?.uri}
                  alt="Before"
                  className={classes.photo}
                />
                {plant.photoUploadStatus === 'pending' && !plant.photos?.before?.url && (
                  <Box style={{ position: 'absolute', top: 8, right: 8, backgroundColor: 'rgba(255,255,255,0.9)', padding: '4px 8px', borderRadius: 4 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" style={{ marginLeft: 8 }}>Uploading...</Typography>
                  </Box>
                )}
              </>
            ) : (
              <Box className={classes.noPhoto}>
                <Image fontSize="large" />
                <Typography>No before photo</Typography>
              </Box>
            )}
          </Box>
          <Typography className={classes.photoLabel}>Before</Typography>
          {(plant.photos?.before?.timestamp || plant.photos?.before?.uploadedAt || plant.timestamps?.imageCaptured) && (
            <Typography variant="caption" display="block" align="center">
              {format(new Date(plant.photos?.before?.timestamp || plant.photos?.before?.uploadedAt || plant.timestamps?.imageCaptured), 'MM/dd/yyyy HH:mm')}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box className={classes.photoContainer}>
            {(plant.photos?.after?.url || plant.photos?.after?.uri) ? (
              <>
                <img
                  src={plant.photos?.after?.url || plant.photos?.after?.uri}
                  alt="After"
                  className={classes.photo}
                />
                {plant.photoUploadStatus === 'pending_after' && !plant.photos?.after?.url && (
                  <Box style={{ position: 'absolute', top: 8, right: 8, backgroundColor: 'rgba(255,255,255,0.9)', padding: '4px 8px', borderRadius: 4 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" style={{ marginLeft: 8 }}>Uploading...</Typography>
                  </Box>
                )}
              </>
            ) : (
              <Box className={classes.noPhoto}>
                <Image fontSize="large" />
                <Typography>No after photo</Typography>
              </Box>
            )}
          </Box>
          <Typography className={classes.photoLabel}>After</Typography>
          {(plant.photos?.after?.timestamp || plant.photos?.after?.uploadedAt || plant.timestamps?.removed) && (
            <Typography variant="caption" display="block" align="center">
              {format(new Date(plant.photos?.after?.timestamp || plant.photos?.after?.uploadedAt || plant.timestamps?.removed), 'MM/dd/yyyy HH:mm')}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );

  const renderLocationTab = () => {
    // Get coordinates from location object
    const lat = plant.location?.latitude || plant.location?.lat;
    const lng = plant.location?.longitude || plant.location?.lng || plant.location?.lon;
    const hasLocation = lat && lng;

    // Create OpenStreetMap embed URL
    const mapUrl = hasLocation
      ? `https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.002},${lat - 0.002},${lng + 0.002},${lat + 0.002}&layer=mapnik&marker=${lat},${lng}`
      : null;

    return (
      <Box className={classes.tabContent}>
        {hasLocation ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Box className={classes.mapContainer}>
                <iframe
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  scrolling="no"
                  marginHeight="0"
                  marginWidth="0"
                  src={mapUrl}
                  style={{ borderRadius: 8 }}
                  title="Plant Location Map"
                />
                <Box
                  style={{
                    position: 'absolute',
                    top: 10,
                    left: 10,
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    padding: '8px 12px',
                    borderRadius: 4,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  <Typography variant="caption" style={{ fontWeight: 'bold', color: plant.type === 'dead' ? '#8b4513' : '#daa520' }}>
                    {plant.type === 'dead' ? '🥀 Dead Plant' : '🍂 Dying Plant'}
                  </Typography>
                  <Typography variant="caption" display="block">
                    ID: {plant.plantId || plant._id?.substring(0, 8)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" className={classes.sectionTitle}>
              <LocationOn /> Location Details
            </Typography>
            <Box className={classes.infoRow}>
              <Typography className={classes.label}>Latitude:</Typography>
              <Typography className={classes.value}>
                {lat.toFixed(6)}
              </Typography>
            </Box>
            <Box className={classes.infoRow}>
              <Typography className={classes.label}>Longitude:</Typography>
              <Typography className={classes.value}>
                {lng.toFixed(6)}
              </Typography>
            </Box>
            {plant.location?.accuracy && (
              <Box className={classes.infoRow}>
                <Typography className={classes.label}>Accuracy:</Typography>
                <Typography className={classes.value}>
                  {plant.location.accuracy.toFixed(2)}m
                </Typography>
              </Box>
            )}
            {plant.location?.altitude && (
              <Box className={classes.infoRow}>
                <Typography className={classes.label}>Altitude:</Typography>
                <Typography className={classes.value}>
                  {plant.location.altitude.toFixed(2)}m
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      ) : (
        <Typography color="textSecondary" align="center">
          No location data available
        </Typography>
      )}
    </Box>
    );
  };

  const renderIrrigationTab = () => (
    <Box className={classes.tabContent}>
      <Typography variant="h6" className={classes.sectionTitle}>
        <Opacity /> Irrigation Details
      </Typography>
      
      {plant.state === 'removed' || plant.state === 'irrigationConfirmed' || plant.state === 'completed' || plant.irrigationLines !== undefined ? (
        <Box className={classes.irrigationSection}>
          {editing ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  className={classes.editField}
                  label="Number of Lines"
                  type="number"
                  fullWidth
                  value={editedPlant.irrigationLines || editedPlant.irrigationDetails?.lines || 0}
                  onChange={(e) => handleFieldChange('irrigationLines', parseInt(e.target.value))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editedPlant.irrigationCapped || editedPlant.irrigationDetails?.capped || false}
                      onChange={(e) => handleFieldChange('irrigationCapped', e.target.checked)}
                    />
                  }
                  label="Lines Capped"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editedPlant.irrigationBuried || editedPlant.irrigationDetails?.buried || false}
                      onChange={(e) => handleFieldChange('irrigationBuried', e.target.checked)}
                    />
                  }
                  label="Lines Buried"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  className={classes.editField}
                  label="Irrigation Notes"
                  multiline
                  rows={3}
                  fullWidth
                  value={editedPlant.irrigationDetails?.notes || ''}
                  onChange={(e) => handleIrrigationChange('notes', e.target.value)}
                />
              </Grid>
            </Grid>
          ) : (
            <>
              <Box className={classes.infoRow}>
                <Typography className={classes.label}>Number of Lines:</Typography>
                <Typography className={classes.value}>
                  {plant.irrigationLines || plant.irrigationDetails?.lines || 0}
                </Typography>
              </Box>
              <Box className={classes.infoRow}>
                <Typography className={classes.label}>Lines Capped:</Typography>
                <Typography className={classes.value}>
                  {(plant.irrigationCapped || plant.irrigationDetails?.capped) ? 'Yes' : 'No'}
                </Typography>
              </Box>
              <Box className={classes.infoRow}>
                <Typography className={classes.label}>Lines Buried:</Typography>
                <Typography className={classes.value}>
                  {(plant.irrigationBuried || plant.irrigationDetails?.buried) ? 'Yes' : 'No'}
                </Typography>
              </Box>
              {(plant.cappedReason || plant.buriedReason) && (
                <Box className={classes.infoRow}>
                  <Typography className={classes.label}>Reason for Capping/Burying:</Typography>
                  <Typography className={classes.value}>
                    {plant.cappedReason || plant.buriedReason}
                  </Typography>
                </Box>
              )}
              {plant.irrigationDetails?.notes && (
                <Box className={classes.infoRow}>
                  <Typography className={classes.label}>Notes:</Typography>
                  <Typography className={classes.value}>
                    {plant.irrigationDetails.notes}
                  </Typography>
                </Box>
              )}
              {plant.irrigationDetails?.confirmedAt && (
                <Box className={classes.infoRow}>
                  <Typography className={classes.label}>Confirmed At:</Typography>
                  <Typography className={classes.value}>
                    {format(new Date(plant.irrigationDetails.confirmedAt), 'MM/dd/yyyy HH:mm')}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      ) : (
        <Typography color="textSecondary" align="center">
          Irrigation details will be available after plant removal
        </Typography>
      )}
    </Box>
  );

  const renderHistoryTab = () => (
    <Box className={classes.tabContent}>
      <Typography variant="h6" className={classes.sectionTitle}>
        <History /> Activity History
      </Typography>
      <List>
        {(plant.history || []).map((entry, index) => (
          <ListItem key={index} className={classes.historyItem}>
            <ListItemIcon>
              <Schedule />
            </ListItemIcon>
            <ListItemText
              primary={entry.action}
              secondary={
                <>
                  {entry.user} - {format(new Date(entry.timestamp), 'MM/dd/yyyy HH:mm')}
                  {entry.details && <Box mt={1}>{entry.details}</Box>}
                </>
              }
            />
          </ListItem>
        ))}
      </List>
      {(!plant.history || plant.history.length === 0) && (
        <Typography color="textSecondary" align="center">
          No history available
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle>
        Plant Details - {plant.plantId || plant._id?.substring(0, 8)}
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Overview" />
          <Tab label="Photos" />
          <Tab label="Location" />
          <Tab label="Irrigation" />
          <Tab label="History" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {renderOverviewTab()}
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          {renderPhotosTab()}
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          {renderLocationTab()}
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          {renderIrrigationTab()}
        </TabPanel>
        <TabPanel value={tabValue} index={4}>
          {renderHistoryTab()}
        </TabPanel>
      </DialogContent>

      <DialogActions>
        {editing ? (
          <>
            <Button onClick={handleCancel} startIcon={<Cancel />}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              color="primary"
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              disabled={saving}
            >
              Save Changes
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>Close</Button>
            <Button
              onClick={handleEdit}
              color="primary"
              variant="contained"
              startIcon={<Edit />}
            >
              Edit
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}